import { type CSSProperties, type ReactNode, useRef } from 'react';
import { Row, type RowProps, theme } from 'antd';
import { PosGridContext } from 'pos-client/contexts/PosGridContext.tsx';
import { useContainerWidth } from 'pos-client/hooks/useContainerWidth.tsx';

/**
 * Props for {@link PosCard}
 */
export interface PosGridProps extends Omit<RowProps, 'children'> {
  /** Grid content @remarks Should only contain `PosColumn` components as children */
  children: ReactNode;
  /** Adjust column responsive properties to the screen or parent element  @defaultValue container */
  responsive?: 'screen' | 'container';
  /** Applies additional columns containing dropdown filters at the top of the element */
  filters?: boolean;
}
/**
 * @component
 * @description Responsive row and column grid
 *
 * @see {@link https://ant.design/components/grid | AntD Grid Component}
 * @param props {@link PosGridProps}
 * @example
 * ```tsx
 * <PosGrid>
    <PosColumn span={12}>
     <PosCard
       headerProps={{title: 'Example Card'}}
     />
    </PosColumn>
    <PosColumn span={12}>
     <PosCard
       headerProps={{title: 'Example Card 2'}}
     />
    </PosColumn>
   </PosGrid>
 * ```
 */
export const PosGrid = ({
  responsive = 'container',
  children,
  filters,
  style,
  ...props
}: PosGridProps) => {
  const rowRef = useRef<HTMLDivElement>(null);
  const { token } = theme.useToken();
  const { currentBreakpoint, breakpointsLess, breakpointsGreater, width } =
    useContainerWidth(rowRef);

  const defaultStyle: CSSProperties = {
    ...(filters && { paddingBottom: token.paddingSM }),
    ...style,
  };

  return (
    <PosGridContext.Provider
      value={{
        responsive,
        currentBreakpoint,
        breakpointsGreater,
        breakpointsLess,
        width,
      }}
    >
      <Row
        ref={rowRef}
        gutter={[token.paddingSM, token.paddingSM]}
        style={defaultStyle}
        {...props}
      >
        {children}
      </Row>
    </PosGridContext.Provider>
  );
};
